import { Controller, FormProvider, useForm } from 'react-hook-form';
import Modal from '../Framework/Components/Modals/Modal';
import { ModalType } from '../Framework/Types/ModalTypes';
import EmailControl from '../Framework/Components/Forms/Email/EmailControl';
import React, { useEffect, useState } from 'react';
import { FormWrapperLabel } from '../Framework/Components/Forms/FormEntryWrapper/FormEntryWrapper';
import { ButtonSubmit } from '../Framework/Components/Buttons/Button';
import { ColourMod } from '../Framework/Data/Enums/LayoutEnums';
import { addAddress, AddressDataUpdateType, useUpdateAddressData } from '../Api/AddressEndpoints';
import CountryCodeControl from './Form/CountryCode';
import { AddressType } from '../Types/AddressType';
import AlertBanner from '../Framework/Components/AlertBanner/AlertBanner';
import TelephoneControl from '../Framework/Components/Forms/Telephone/TelephoneControl';

type AddAddressType = {
	existingData?: AddressType;
	onAddCallback?: any;
};

const AddAddress = (props: AddAddressType) => {
	const formMethods = useForm({
		defaultValues: {
			name: props.existingData?.name ?? '',
			businessName: props.existingData?.businessName ?? '',
			propertyName: props.existingData?.propertyName ?? '',
			addressLine1: props.existingData?.addressLine1 ?? '',
			addressLine2: props.existingData?.addressLine2 ?? '',
			addressLine3: props.existingData?.addressLine3 ?? ' ',
			addressLine4: props.existingData?.addressLine4 ?? '',
			postCode: props.existingData?.postCode ?? '',
			countryCode: props.existingData?.countryCode ?? '',
			phoneNo: props.existingData?.phoneNo ?? '',
			emailAddress: props.existingData?.emailAddress ?? '',
			flatNumber: props.existingData?.flatNumber ?? '',
			propertyNumber: props.existingData?.propertyNumber ?? '',
		},
		mode: 'onChange',
	});

	const {
		control,
		register,
		handleSubmit,
		setError,
		getValues,
		clearErrors,
		setValue,
		reset,
		setFocus,
		formState: { errors, isValid },
	} = formMethods;

	const [addressError, setAddressError] = useState<any>();

	const [addressSaving, setAddressSaving] = useState<boolean>(false);
	const [telLookupActive, setTelLookupActive] = useState<boolean>(false);
	const [emailLookupActive, setEmailLookupActive] = useState<boolean>(false);

	const updateAddressData = useUpdateAddressData();

	const HandleSave = (data: any, event: any) => {
		setAddressSaving(true);

		addAddress(data)
			.then((r: any) => {
				updateAddressData.mutate({
					newData: {
						value: r.addressId,
						object: { ...r, ...data },
						label: `${data.name}, ${data.propertyNameNumber}, ${data.addressLine1}`,
					},
					updateType: AddressDataUpdateType.CREATE,
				});

				if (props.onAddCallback) {
					props.onAddCallback({ ...data, addressId: r.addressId });
				}

				reset();
			})
			.catch((e) => {
				console.log(e);
				setAddressError(e.toString());
			})
			.finally(() => setAddressSaving(false));
	};

	const BuildPropertyNameNumber = () => {
		let newString = '';

		if (props.existingData?.propertyNumber) {
			if (props.existingData?.propertyName) {
				newString += `${props.existingData?.propertyName}`;

				if (props.existingData?.flatNumber) {
					newString += `, ${props.existingData?.flatNumber}`;
				}

				if (props.existingData?.propertyNumber) {
					newString += `, ${props.existingData?.propertyNumber}`;
				}
			} else if (props.existingData?.flatNumber) {
				newString += `${props.existingData?.flatNumber}`;

				if (props.existingData?.propertyNumber) {
					newString += `, ${props.existingData?.propertyNumber}`;
				}
			} else if (props.existingData?.propertyNumber) {
				newString += `${props.existingData?.propertyNumber}`;
			}
		}

		return newString;
	};

	useEffect(() => {
		if (props.existingData) {
			setValue('name', props.existingData.name);
			setValue('businessName', props.existingData.businessName);
			setValue('propertyName', props.existingData.propertyName);
			setValue('addressLine1', props.existingData.addressLine1);
			setValue('addressLine2', props.existingData.addressLine2);
			setValue('addressLine3', props.existingData.addressLine3);
			setValue('addressLine4', props.existingData.addressLine4);
			setValue('postCode', props.existingData.postCode);
			setValue('countryCode', props.existingData.countryCode);
			setValue('phoneNo', props.existingData.phoneNo);
			setValue('emailAddress', props.existingData.emailAddress);
			setValue('flatNumber', props.existingData.flatNumber as string);
			setValue('propertyNumber', props.existingData.propertyNumber as string);
		} else {
			setValue('name', '');
			setValue('businessName', '');
			setValue('propertyName', '');
			setValue('addressLine1', '');
			setValue('addressLine2', '');
			setValue('addressLine3', '');
			setValue('addressLine4', '');
			setValue('postCode', '');
			setValue('countryCode', '');
			setValue('phoneNo', '');
			setValue('emailAddress', '');
			setValue('flatNumber', '');
			setValue('propertyNumber', '');
		}
		setFocus('name');
	}, [props]);

	return (
		<FormProvider {...formMethods}>
			<form onSubmit={handleSubmit((data, e) => HandleSave(data, e))}>
				<fieldset className={'flex-column flex-gap-md'}>
					<FormWrapperLabel
						label={'Name'}
						value={getValues().name}
						invalid={errors.name && errors.name?.message !== ''}
						validationMessage={errors.name?.message as string}
					>
						<input
							type='text'
							aria-invalid={errors.name ? 'true' : 'false'}
							{...register('name', {
								required: 'This is a required field',
								minLength: { value: 1, message: 'Too short' },
							})}
						/>
					</FormWrapperLabel>

					<FormWrapperLabel
						label={'Property Name/Number'}
						value={getValues().propertyName}
						invalid={errors.propertyName && errors.propertyName?.message !== ''}
						validationMessage={errors.propertyName?.message as string}
					>
						<input
							type='text'
							aria-invalid={errors.propertyName ? 'true' : 'false'}
							{...register('propertyName', {
								required: 'This is a required field',
								minLength: { value: 1, message: 'Must be longer than 1 character' },
							})}
						/>
					</FormWrapperLabel>

					<FormWrapperLabel
						label={'Street'}
						value={getValues().addressLine1}
						invalid={errors.addressLine1 && errors.addressLine1?.message !== ''}
						validationMessage={errors.addressLine1?.message as string}
					>
						<input
							type='text'
							aria-invalid={errors.addressLine1 ? 'true' : 'false'}
							{...register('addressLine1', {
								required: 'This is a required field',
								minLength: { value: 2, message: 'Must be longer than 2 characters' },
							})}
						/>
					</FormWrapperLabel>

					<FormWrapperLabel
						label={'Town/City'}
						value={getValues().addressLine2}
						invalid={errors.addressLine2 && errors.addressLine2?.message !== ''}
						validationMessage={errors.addressLine2?.message as string}
					>
						<input
							type='text'
							aria-invalid={errors.addressLine2 ? 'true' : 'false'}
							{...register('addressLine2', {
								required: 'This is a required field',
								minLength: { value: 2, message: 'Must be longer than 2 characters' },
							})}
							onChange={(e) => setValue('addressLine3', e.currentTarget.value)}
						/>
					</FormWrapperLabel>

					<FormWrapperLabel
						label={'County'}
						value={getValues().addressLine4}
						invalid={errors.addressLine4 && errors.addressLine4?.message !== ''}
						validationMessage={errors.addressLine4?.message as string}
					>
						<input
							type='text'
							aria-invalid={errors.addressLine4 ? 'true' : 'false'}
							{...register('addressLine4', {
								required: 'This is a required field',
								minLength: { value: 2, message: 'Must be longer than 2 characters' },
							})}
						/>
					</FormWrapperLabel>

					<FormWrapperLabel
						label={'Post Code'}
						value={getValues().postCode}
						invalid={errors.postCode && errors.postCode?.message !== ''}
						validationMessage={errors.postCode?.message as string}
					>
						<input
							type='text'
							aria-invalid={errors.postCode ? 'true' : 'false'}
							{...register('postCode', {
								required: 'This is a required field',
								minLength: { value: 2, message: 'Must be longer than 2 characters' },
							})}
						/>
					</FormWrapperLabel>

					<Controller
						control={control}
						name='countryCode'
						defaultValue={getValues().countryCode}
						rules={{ required: 'Country is required' }}
						render={({ field, fieldState }) => (
							<CountryCodeControl
								label={'Country'}
								placeholder={'Country'}
								name={'Country'}
								onChange={(val: any) => field.onChange(val.value)}
							/>
						)}
					/>

					<TelephoneControl
						label={'Telephone Number'}
						name={`phoneNo`}
						defaultValue={getValues().phoneNo}
						forwardStatus={(e: any) => setTelLookupActive(e)}
						required
					/>

					<EmailControl
						control={control}
						name={'emailAddress'}
						invalid={errors.emailAddress && errors.emailAddress?.message !== ''}
						validationMessage={errors.emailAddress?.message as string}
						defaultValue={getValues().emailAddress}
						forwardBlocker={(e: boolean) => setEmailLookupActive(e)}
						setError={setError}
						clearErrors={clearErrors}
						required
					/>

					{addressError &&
						Object.entries(addressError).map((error: any, index: number) => (
							<AlertBanner
								key={`addAddressError_${index}`}
								colourMod={ColourMod.Alert}
								title={'Error'}
								content={error.toString()}
							/>
						))}

					<ButtonSubmit
						title={`${props.existingData ? 'Update' : 'Add'} Address`}
						loading={addressSaving}
						disabled={!isValid || emailLookupActive || telLookupActive}
						fullWidth={true}
						colour={ColourMod.Primary}
					/>
				</fieldset>
			</form>
		</FormProvider>
	);
};

export default AddAddress;

interface AddAddressModalType extends ModalType {
	existingData?: any;
}

const AddAddressModal = (props: AddAddressModalType) => {
	return (
		<Modal title={'New Address'} isOpen={props.visible} closeCallback={() => props.closeCallback()}>
			<AddAddress
				existingData={props.existingData}
				onAddCallback={(id: number) =>
					props.actionedCallback ? props.actionedCallback(id) : props.closeCallback()
				}
			/>
		</Modal>
	);
};

export { AddAddressModal };
